<template>
    <div class="page-wrapper v-cloak">
        <!-- Hero image and model -->
        <div class="page-grid-fix background-img top-blurb" style="background-image: linear-gradient(rgba(0, 0, 0, 0.527), rgba(0, 0, 0, 0.7)), url(/uploads/Ninety_RM_Workshop_2020-11-06_10.jpg);">
            <div class="model-title">
                <h1 style="color: white !important;">{{ currentModel }}</h1>
                <div>{{ modelName }}</div>
            </div>
        </div>
        <!-- Model information -->
        <div class="page-grid-wrapper">
            <div class="page-grid-fix">
                <div class="model-sections">
                    <div
                        class="section fade-section"
                        v-for="(section, index) in sections"
                        :key="section.title"
                        :ref="el => sectionsRefs[index] = el"
                    >
                        <!-- Video section -->
                        <div class="section-content-wrap section-video-wrapper" v-if="section.video">
                            <h2>{{ section.title }}</h2>
                            <div class="section-video">
                                <video  playsinline autoplay muted loop :src="section.video"></video>
                            </div>
                            <div class="section-content" v-html="section.content"></div>
                        </div>

                        <!-- Image section -->
                        <div class="section-content-wrap section-img-wrapper" v-else>
                            <h2>{{ section.title }}</h2>
                            <div class="section-img">
                                <img  v-if="section.img" :src="section.img" alt="Placeholder" />
                            </div>
                            <div class="section-content" v-html="section.content"></div>
                        </div>

                    </div>
                </div>
            </div>
        </div>

        <!-- Call to action -->
        <div class="cfa-sentinel"></div><!-- helps to stick at the bottom of the viewport-->
        <div class="model-CFA">
            <div><span>“Engineered for the skies, born for your wrist - the RM 21-02.”</span>
                <a href="https://dev.ninetywatchesandjewellery.com/contact" class="">Enquire
                </a>
                <a href="https://dev.ninetywatchesandjewellery.com/locations" class="">Visit our boutique
                </a>
                </div>
        </div>

        <!-- Currently available -->
        <div class="model-list-available" v-if="currentlyAvailable.length">
            <h3>Currently available</h3>
            <div class="grid-view">
                <div v-for="watch in currentlyAvailable" :key="watch.SKU" class="watch-card">
                <router-link :to="{ name: 'watchModel', params: { watchModel: watch.Model.replace(/ /g, '-model-') } }">
                    <img :src="`/watches/gallery/NW-${watch.SKU}/grid-image/grid_NW-${watch.SKU}.jpg`" />
                </router-link>
                <div>{{ watch.Material }}</div>
                <div><i>in</i> {{ watch.WatchLocation }}</div>
                </div>
            </div>
        </div>

        <!-- Previously available -->
        <div class="model-list-sold" v-if="previouslyAvailable.length">
            <h3>Previously available</h3>
            <div class="grid-view">
                <div v-for="watch in previouslyAvailable" :key="watch.SKU" class="watch-card">
                <router-link :to="{ name: 'watchModel', params: { watchModel: watch.Model.replace(/ /g, '-model-') } }">
                    <img :src="`/watches/gallery/NW-${watch.SKU}/grid-image/grid_NW-${watch.SKU}.jpg`" />
                </router-link>
                <div>{{ watch.Material }}</div>
                <small v-if="watch.WatchLocation" style="text-transform: initial;">
                    In our
                    <router-link :to="{ path: '/locations', hash: '#' + watch.WatchLocation }"
                                style="color: #ae9248; text-transform: capitalize;">
                    {{ watch.WatchLocation }}
                    </router-link>
                    boutique
                </small>
                </div>
            </div>
        </div>


        <!-- Empty fallback -->
        <div v-if="!watches.length">
            <p>No watches found for this model.</p>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";

export default {
  name: "WatchModel",
  props: ["watchModel"],
  data() {
    return {
      watches: [],
      currentModel: null,
      axiosCancelToken: null,
      loaderTimeout: null,
      sectionsRefs: [], // keep track of all section refs
      modelName: "Manual Winding Tourbillon Aerodyne",
      sections: [
        {
          title: "Introduction",
          content: `The RM 21-02 continues Richard Mille’s aeronautics-inspired series, first launched in 2022 in Green Quartz TPT®. Each version is limited to just 50 pieces.`,
          img: "/watches/gallery/NW-01383/grid-image/grid_NW-01383.png",
        },
        {
          title: "The Story / Heritage",
          content: `The RM 21-02 evolves from the RM 21-01, expanding the Aerodyne line that originally began with the RM 021.<br>
                    The series takes its name and structure from aeronautical engineering, with materials and design elements taken directly from aerospace applications.`,
          img: "/watches/gallery/NW-01383/gallery-large-image/NW-01383_NW-01383_Social_Media_RM21_02_Ti_CA_FQ_014.jpg",
        },
        {
          title: "Design & Craftsmanship",
          content: `<strong>Case Materials</strong><br>
                    • 2022: Green Quartz TPT® with Carbon TPT® bezel and pillars.<br>
                    • 2023: Black and White edition in White Quartz TPT® and Carbon TPT®.<br>
                    • 2024: Blue Caribbean Quartz TPT® with titanium case.<br><br>
                    <strong>Case Construction</strong><br>
                    • Dimensions: 42.68 x 50.12 mm. Thickness: 14.30 mm.<br>
                    • Carbon TPT® exoskeleton for reinforcement.<br>
                    • Nitrile O-ring seals, secured with 20 titanium spline screws.<br><br>
                    <strong>Baseplate</strong><br>
                    • Honeycombed orthorhombic Haynes® 214® alloy with Carbon TPT® core.<br>
                    • PVD-treated in black and grey for durability and contrast.`,
          video: "/videos/NW-01383.mov",
        },
        {
          title: "Technical Innovations",
          content: `<strong>Calibre RM 21-02</strong><br>
                    • Manual-winding tourbillon.<br>
                    • Functions: hours, minutes, power-reserve, torque indicator, function selector.<br>
                    • Power reserve ~70 hours.<br><br>
                    <strong>Engineering Features</strong><br>
                    • Skeletonized architecture for reduced weight and visibility of components.<br>
                    • Function selector switches between winding, neutral, and time-setting.<br>
                    • Torque-limiting crown prevents overwinding.<br>
                    • Shock resistance designed for high-impact performance.`,
          img: "/watches/gallery/NW-01383/gallery-large-image/NW-01383_NW-01383_Social_Media_RM21_02_Ti_CA_FQ_012.jpg",
        },
        {
          title: "Iconic References / Important Pieces",
          content: `First Richard Mille model to use a Haynes® 214® baseplate combined with Carbon TPT®.<br>
                    Produced only in limited runs of 50 pieces per edition.`,
          img: "/watches/gallery/NW-01383/gallery-large-image/NW-01383_NW-01383_Social_Media_RM21_02_Ti_CA_FQ_012.jpg",
        },
        {
          title: "Cultural Significance",
          content: `The RM 21-02 is positioned within Richard Mille’s high-engineering collection rather than ambassador-led releases.<br>
                    It has not been tied to a specific athlete or celebrity, underlining its role as a technical showcase within the brand’s catalogue.`,
          img: "/watches/gallery/NW-01383/gallery-large-image/NW-01383_NW-01383_Social_Media_RM21_02_Ti_CA_FQ_012.jpg",
        },
      ],
    };
  },
   computed: {
    ...mapGetters("auth", ["authUser"]),
    previouslyAvailable() {
      return this.watches.filter(w => w.Status === 0);
    },
    currentlyAvailable() {
      return this.watches.filter(w => w.Status === 1);
    },
  },
  created() {
    if (this.$route.params.watchModel) {
      this.decodeAndLoad(this.$route.params.watchModel);
    }
  },
  watch: {
    "$route.params.watchModel"(newModel) {
      this.decodeAndLoad(newModel);
    },
  },
  mounted() {
    this.observeSections();
    this.observeCFA();
  },
  methods: {
    decodeAndLoad(param) {
      const decoded = param.replace(/-model-/g, " ");
      this.currentModel = decoded;
      this.loadModelWatches(decoded);
    },
    async loadModelWatches(model) {
      if (this.axiosCancelToken) this.axiosCancelToken.cancel("Canceled due to new request.");
      this.axiosCancelToken = axios.CancelToken.source();

      clearTimeout(this.loaderTimeout);
      this.loaderTimeout = setTimeout(() => {
        this.$emit("route-data-ready");
      }, 200);

      try {
        const response = await this.$axios.get(`/api/watchmodel/${encodeURIComponent(model)}`, {
          cancelToken: this.axiosCancelToken.token,
        });
        this.watches = response.data;
        this.$emit("route-data-ready");
      } catch (err) {
        if (!axios.isCancel(err)) console.error("Failed to load watches:", err);
        this.$emit("route-data-ready");
      } finally {
        clearTimeout(this.loaderTimeout);
      }
    },
    observeSections() {
      const wrapper = this.$el.querySelector(".model-sections");
      if (!wrapper) return;

      const sections = wrapper.querySelectorAll(".fade-section");
      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add("visible");
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.2 }
      );

      sections.forEach(sec => observer.observe(sec));
      this._sectionObserver = observer;
    },
    observeCFA() {
      const cfaEl = this.$el.querySelector(".model-CFA");
      const sentinel = this.$el.querySelector(".cfa-sentinel");
      if (!cfaEl || !sentinel) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) {
            cfaEl.classList.add("is-stuck");
          } else {
            cfaEl.classList.remove("is-stuck");
          }
        },
        { threshold: [0] }
      );
      observer.observe(sentinel);
      this._cfaObserver = observer;
    },
  },
  beforeUnmount() {
    if (this._sectionObserver) this._sectionObserver.disconnect();
    if (this._cfaObserver) this._cfaObserver.disconnect();
    clearTimeout(this.loaderTimeout);
    if (this.axiosCancelToken) this.axiosCancelToken.cancel("Component destroyed, canceling request.");
  },
};
</script>

<style scoped lang="scss">

// observer sections styles
.page-grid-fix:first-of-type {
  margin-top: 0 !important;
}
.model-sections .fade-section {
  opacity: 0;
//   transform: translateY(40px); // slide up on load
  transition: opacity 0.8s ease, transform 0.8s ease;
}
.model-sections .fade-section.visible {
  opacity: 1;
  transform: translateY(0);
}
.fade-section img {
  opacity: 0;
//   transform: rotate(-5deg) scale(0.95);
  transition: transform 0.8s ease, opacity 0.8s ease;
}
.fade-section.visible img {
  opacity: 1;
//   transform: rotate(0deg) scale(1);
}

// module styles

#main {
    // Top blurb
  .page-wrapper {
    margin-top: 100px;
    .top-blurb {
      padding-bottom: 0;
        .model-title {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100%;
            width: 100%;
            text-align: center;
            flex-direction: column;
            h1 {
                font-size: 4.4rem;
                padding-bottom: 0px;
            }
            div {
                font-size: 1.4em;
                color: white !important;
                letter-spacing: 0.2em;
            }
        }
    }
  }

  // Sections styles
  .model-sections {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        padding: 1.5rem 0;
        @media (min-width: 768px) { 
             padding: 1.5rem 1.5rem;
        }

      .section {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        padding: 1.5rem 0;
        @media (min-width: 768px) { 
             padding: 1.5rem 1.5rem;
        }
        // First section - Introduction

        &:first-of-type div.section-content-wrap {
             .section-img {
                    img {
                        box-shadow: 0px 0px 0 rgba(128, 128, 128, 0);
                    }
                }
             @media (min-width: 768px) {
                grid-template-columns: repeat(2, 1fr); /* Two columns on larger screens */

                .section-img {
                    grid-column: 1; /* Default video to the first column */
                    grid-row: 2; /* Place video after the h2, by default */
                    img {
                        box-shadow: 0px 0px 0 rgba(128, 128, 128, 0);
                    }
                }

                .section-content {
                    grid-column: 2; /* Default content to the second column */
                    grid-row: 2; /* Place content after the h2, by default */
                    align-self: flex-end;
                }
            }
        }
    }


      /* ---- IMAGE LAYOUT ---- */
      .section:nth-of-type(3n) div.section-img-wrapper {

        @media (min-width: 768px) {
            grid-template-columns: repeat(2, 1fr); /* Two columns on larger screens */

            .section-img {
                grid-column: 2; /* Default video to the first column */
                grid-row: 2; /* Place video after the h2, by default */
            }

            .section-content {
                grid-column: 1; /* Default content to the second column */
                grid-row: 2; /* Place content after the h2, by default */
            }
        }
    }
    .section:nth-of-type(4n) div.section-img-wrapper {

        @media (min-width: 768px) {
            grid-template-columns: repeat(2, 1fr); /* Two columns on larger screens */

            .section-img {
                grid-column: 1; /* Default video to the first column */
                grid-row: 2; /* Place video after the h2, by default */
            }

            .section-content {
                grid-column: 2; /* Default content to the second column */
                grid-row: 2; /* Place content after the h2, by default */
            }
        }
    }

    .section-content-wrap.section-img-wrapper {
        display: grid;
        grid-template-columns: 1fr; /* Default to a single column on smaller screens */
        grid-template-rows: auto;
        grid-gap: 25px; /* Add some space between the grid items */
                /* Basic styling for the video and content (you can customize these) */
        .section-img {
            width: 100%;
            height: auto;
            margin: 0;
            img {
                max-width: 100%;
                box-shadow: 0px 0px 15px gray;
            }
        }

        /* Make sure the h2 is always at the top */
        h2 {
            grid-column: 1 / -1; /* Span the entire width */
            // text-align: left;
        }

        /* Media query for larger screens */
        @media (min-width: 768px) {
            grid-template-columns: repeat(1, 1fr); /* Two columns on larger screens */

            .section-img {
            grid-column: 1; /* Default video to the first column */
            grid-row: 2; /* Place video after the h2, by default */
            }

            .section-content {
            grid-column: 1; /* Default content to the second column */
            grid-row: 3; /* Place content after the h2, by default */
            }
        }

    }

      /* ---- VIDEO LAYOUT ---- */
      
    .section-content-wrap.section-video-wrapper {
        display: grid;
        grid-template-columns: 1fr; /* Default to a single column on smaller screens */
        grid-template-rows: auto;
        grid-gap: 25px; /* Add some space between the grid items */
                /* Basic styling for the video and content (you can customize these) */
        .section-video {
            width: 100%;
            margin: 0;
            padding: 0;
            line-height: 0;
            box-shadow: 0px 0px 15px gray;
        video {
            position: relative;
            left: initial;
            top: initial;
            transform: none;
            height: 100%;
            min-width: auto;
            min-height: auto;
            max-width: 100%;
            }
        }

        /* Make sure the h2 is always at the top */
        h2 {
            grid-column: 1 / -1; /* Span the entire width */
            // text-align: left;
        }

        /* Media query for larger screens */
        @media (min-width: 768px) {
            grid-template-columns: repeat(1, 1fr); /* Two columns on larger screens */

            .section-video {
            grid-column: 1; /* Default video to the first column */
            grid-row: 2; /* Place video after the h2, by default */
            }

            .section-content {
            grid-column: 1; /* Default content to the second column */
            grid-row: 3; /* Place content after the h2, by default */
            }

            /* Example: To swap the order of video and content */
            &.video-first {
            .section-video {
                grid-column: 2;
            }
            .section-content {
                grid-column: 1;
            }
            }
        }
    }

      .section-content-wrap {
        border-bottom: 1px solid gray;
        padding-bottom: 85px;
        display: flex;  
        flex-flow: row wrap;
        font-weight: bold;
        text-align: center;
        h2 {
          text-transform: uppercase;
          margin-bottom: 1rem;
          color: #222 !important;
          text-align: center;
        }
        div.section-content::first-line {
            font-weight: bold;
        }
        .section-content {
          text-align: left;
          font-size: 1rem;
          color: #555;
          line-height: 2;
          max-width: 780px;
          letter-spacing: 0.1em;
        }

      }

    }
  

  // CFA ribbon styles
  .cfa-sentinel {
        height: 1px; /* Ensure it has some height */
        width: 100%; /* Full width */
    }
    .model-CFA {
        position: sticky;
        bottom: 0;
        padding: 20px;
        text-align: center;
        z-index: 10;
        // height: 80px;
        width: 100vw;
        background-color: #ae9248;
        left: 0;
        transition: all 0.3s ease;
        > div {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            // grid-template-rows: auto;
            // grid-gap: 25px;
            align-items: center;
        }
        &.is-stuck {
            padding: 10px 0;
            box-shadow: 0 -4px 15px rgba(0, 0, 0, 0.3);
            height: auto;
            a {
                padding: 0 4vw;
            }
        }
    span {
        border-right: 1px solid #ffffff80;
        padding-right: 25px;
      font-style: italic;
      display: inline;
      color: white !important;
      font-family: Georgia, 'Times New Roman', Times, serif;
      font-size: 1.2rem;
      opacity: 0.7;
    }
    a {
      text-transform: uppercase;
      letter-spacing: 2px;
      font-size: 1rem;
    }
    span, a {
      padding-left: 25px;
      color: white !important;
    }
  }


 // Ninety's watches section
  .grid-view {
    grid-template-columns: repeat(2, 1fr);
    @media (min-width: 768px) {
        grid-template-columns: repeat(6, 1fr);
    }
    .watch-card {
      display: flex;
      flex-direction: column;
      text-align: center;
      align-self: baseline;
      img {
        width: 100%;
      }
    }
  }

  .model-list-sold {
    .grid-view, h3 {
      background-color: black;
      padding: 25px 0 0 25px;
      div {
        color: white;
      }
    }
  }

}
</style>
